import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useId, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import { useFlickerGuard } from '../../hooks/useFlickerGuard';
const LoadingContext = createContext(null);
/**
 * Singleton-skeleton provider. Renders the app's `skeleton` element
 * over its children whenever any descendant calls `useRegisterLoading(true)`,
 * so adjacent loading layers (auth check → workspace check → onboarding, etc.)
 * hand off seamlessly — the skeleton stays mounted across the chain, the
 * animation never resets, and there is no inter-guard flicker.
 *
 * Min-visible (300ms) and delayed-show (100ms) gating is applied once at the
 * provider against the *aggregate* loading state, not per-source.
 */
export function LoadingSkeletonProvider({ children, skeleton, minVisibleMs, delayShowMs, hideGraceMs = 500, }) {
    const [activeCount, setActiveCount] = useState(0);
    const idsRef = useRef(new Set());
    const register = useCallback((id) => {
        if (idsRef.current.has(id))
            return;
        idsRef.current.add(id);
        setActiveCount(idsRef.current.size);
    }, []);
    const unregister = useCallback((id) => {
        if (!idsRef.current.delete(id))
            return;
        setActiveCount(idsRef.current.size);
    }, []);
    const value = useMemo(() => ({ register, unregister }), [register, unregister]);
    const showSkeleton = useFlickerGuard(activeCount > 0, minVisibleMs, delayShowMs, hideGraceMs);
    // Keep the skeleton mounted at all times and toggle visibility via CSS, so
    // a transient unmount of the visible-state doesn't restart the skeleton's
    // SMIL timeline. When hidden, the skeleton is taken out of layout flow
    // (position: fixed) and made non-interactive so it doesn't block clicks.
    return (_jsxs(LoadingContext.Provider, { value: value, children: [_jsx("div", { "aria-hidden": !showSkeleton, style: {
                    position: 'fixed',
                    inset: 0,
                    zIndex: 50,
                    visibility: showSkeleton ? 'visible' : 'hidden',
                    pointerEvents: showSkeleton ? 'auto' : 'none',
                }, children: skeleton }), children] }));
}
/**
 * Registers a loading source with the nearest `LoadingSkeletonProvider`.
 * Returns `true` if a provider is in the tree, `false` otherwise — callers
 * can use that to decide whether to render a local fallback (no provider)
 * or delegate to the singleton (provider in tree).
 *
 * Registration happens in `useLayoutEffect`, so the provider re-renders
 * before paint — no blank-frame gap when a guard mounts mid-load.
 */
export function useRegisterLoading(loading) {
    const ctx = useContext(LoadingContext);
    const id = useId();
    const hasProvider = ctx !== null;
    useLayoutEffect(() => {
        if (!ctx)
            return;
        if (loading) {
            ctx.register(id);
            return () => ctx.unregister(id);
        }
    }, [ctx, id, loading]);
    return hasProvider;
}
//# sourceMappingURL=LoadingSkeletonProvider.js.map